import {
  CPUS,
  FAN_WATTS,
  findModel,
  GPUS,
  MEMORY_WATTS,
  type PartModel,
  PLATFORM_WATTS,
  STORAGE_WATTS,
} from './parts'
import {
  type Build,
  CUSTOM_MODEL,
  isValidCustomWatts,
  isValidOtherWatts,
  type ModelChoice,
  NO_GPU,
} from './spec'

/**
 * How much a build draws, and what power supply it needs.
 *
 * Pure and DOM-free like every model module; the components only display it.
 */

/** Display order: the two parts that decide the answer first. */
export const LOAD_CATEGORIES = [
  'cpu',
  'gpu',
  'board',
  'memory',
  'storage',
  'fans',
  'other',
] as const
export type LoadCategory = (typeof LOAD_CATEGORIES)[number]

/**
 * Share of its peak each kind of part draws in light use — desktop work, browsing,
 * video. Processors and graphics cards idle at a small fraction of their rating;
 * the board, memory and fans change far less. Rough by nature, and deliberately
 * on the high side: an energy estimate that comes in under the bill is worse than
 * one that comes in over it.
 */
export const LIGHT_USE_SHARE: Readonly<Record<LoadCategory, number>> = {
  cpu: 0.2,
  gpu: 0.1,
  board: 0.5,
  memory: 0.5,
  storage: 0.3,
  fans: 0.5,
  // Unknown parts: the same middle share as the board, erring high.
  other: 0.5,
}

export interface LoadEstimate {
  /** Peak draw of each kind of part, in watts. */
  readonly parts: Readonly<Record<LoadCategory, number>>
  /** Everything at full load at once: what the power supply must be able to deliver. */
  readonly peakWatts: number
  /** Typical draw in light use, for the energy estimate. */
  readonly lightWatts: number
}

/**
 * Watts for one processor or graphics card, or `null` when it cannot be known —
 * an id no catalogue holds (a link from a newer version), or a custom wattage
 * that is blank or absurd.
 */
export function modelWatts(choice: ModelChoice, catalogue: readonly PartModel[]): number | null {
  if (choice.model === CUSTOM_MODEL) {
    return isValidCustomWatts(choice.customWatts) ? choice.customWatts : null
  }
  if (choice.model === NO_GPU) return 0
  return findModel(catalogue, choice.model)?.watts ?? null
}

/** Returns `null` for a build with an unknowable part — never throws. */
export function estimateLoad(build: Build): LoadEstimate | null {
  // Integrated graphics is part of the processor's rating, so `none` is not a CPU option.
  if (build.cpu.model === NO_GPU) return null
  const cpu = modelWatts(build.cpu, CPUS)
  const gpu = modelWatts(build.gpu, GPUS)
  if (cpu === null || gpu === null || !isValidOtherWatts(build.otherWatts)) return null

  const parts: Record<LoadCategory, number> = {
    cpu: cpu * build.cpu.count,
    gpu: gpu * build.gpu.count,
    board: PLATFORM_WATTS[build.platform],
    memory: MEMORY_WATTS[build.memory.type] * build.memory.count,
    storage: build.storage.reduce((sum, line) => sum + STORAGE_WATTS[line.type] * line.count, 0),
    fans: FAN_WATTS * build.fans,
    other: build.otherWatts,
  }

  let peakWatts = 0
  let lightWatts = 0
  for (const category of LOAD_CATEGORIES) {
    peakWatts += parts[category]
    lightWatts += parts[category] * LIGHT_USE_SHARE[category]
  }
  return { parts, peakWatts, lightWatts }
}

/**
 * The highest share of its rating the peak load should reach.
 *
 * Leaving a fifth spare covers what a sum of ratings misses: graphics cards spike
 * well past their board power for fractions of a second (ATX 3.x supplies are
 * specified to ride out such excursions), capacitors age, and parts get added
 * later. A unit
 * running below its limit also sits nearer its efficiency peak, cooler and quieter.
 */
export const TARGET_LOAD = 0.8

/** Ratings retailers actually stock. A recommendation is always one of these. */
export const PSU_SIZES = [
  300, 350, 400, 450, 500, 550, 600, 650, 750, 850, 1000, 1200, 1300, 1500, 1600,
] as const

export interface PsuRecommendation {
  /** Rated output of the recommended unit, in watts. */
  readonly watts: number
  /** Peak load as a fraction of that rating. */
  readonly loadShare: number
  /**
   * False when the build needs more than the largest common unit: `watts` is then
   * the requirement rounded up to 100 W, and the build wants two supplies or a
   * server unit.
   */
  readonly standard: boolean
}

export function recommendPsu(peakWatts: number): PsuRecommendation {
  const needed = peakWatts / TARGET_LOAD
  const size = PSU_SIZES.find((candidate) => candidate >= needed)
  const watts = size ?? Math.ceil(needed / 100) * 100
  return { watts, loadShare: peakWatts / watts, standard: size !== undefined }
}
